import styled from 'styled-components'

import { _XS_Medium, _XS_Normal, mainColors, semanticColors } from '@avantstay/backoffice-core'

export type ApprovalCardState = 'pending' | 'approved' | 'rejected' | 'expired'

const CARD_TONES: Record<ApprovalCardState, { border: string; background: string }> = {
  pending: { border: semanticColors.warningMedium, background: semanticColors.warningExtraLight },
  approved: { border: semanticColors.successLight, background: semanticColors.successExtraLight },
  rejected: { border: semanticColors.failureLight, background: semanticColors.failureExtraLight },
  expired: { border: mainColors.neutralMedium, background: mainColors.neutralExtraLight },
}

// Replaces the regular chat bubble for approval messages: one tinted box that
// shifts tone with the decision (orange pending, green approved, red rejected).
export const Card = styled.div<{ $state: ApprovalCardState }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ $state }) => CARD_TONES[$state].border};
  border-radius: 8px;
  background: ${({ $state }) => CARD_TONES[$state].background};
`

export const Action = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const Headline = styled.div`
  ${_XS_Medium}
  color: ${mainColors.neutralExtremelyDark2};
`

export const ToolName = styled.code`
  ${_XS_Medium}
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.7);
  color: ${mainColors.accentDark};
`

export const Facts = styled.div`
  ${_XS_Normal}
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: ${mainColors.neutralExtraDark};

  & strong {
    color: ${mainColors.neutralExtremelyDark2};
  }
`

// Native disclosure keeps the arguments collapsed by default with zero JS.
export const ArgsDetails = styled.details`
  border-radius: 6px;
`

export const ArgsSummary = styled.summary`
  ${_XS_Medium}
  cursor: pointer;
  color: ${mainColors.accentDark};
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
`

export const Args = styled.pre`
  ${_XS_Normal}
  max-height: 180px;
  margin: 6px 0 0;
  padding: 8px;
  overflow: auto;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.7);
  color: ${mainColors.neutralExtremelyDark2};
  white-space: pre-wrap;
  word-break: break-word;
`

export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`

export const ApproveButton = styled.button`
  ${_XS_Medium}
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: ${semanticColors.successMedium};
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

export const RejectButton = styled.button`
  ${_XS_Medium}
  padding: 6px 14px;
  border: 1px solid ${mainColors.neutralMedium};
  border-radius: 6px;
  background: ${mainColors.white};
  color: ${mainColors.neutralExtremelyDark2};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

export const ResolvedNote = styled.div`
  ${_XS_Normal}
  color: ${mainColors.neutralExtraDark};
`
